import { Component, OnInit, ElementRef, HostBinding } from '@angular/core';
import { NavigationEnd, Router, UrlSerializer, UrlTree } from '@angular/router';
import { UserService } from '@deltager/organizers/admin/core/services/user.service';
import { AppSettingsService } from '@deltager/organizers/admin/core/app-settings/app-settings.service';
import { load } from './core/locale';
import { registerLocaleData } from '@angular/common';
import { AffiliateService } from './core/services/affiliate.service';
import { Store } from '@ngrx/store';
import { selectEditContentMode, SwitchEditContentAction } from './redux/root.state';
import { distinctUntilChanged, filter } from 'rxjs';
import { ConfigCatService } from './core/services/config-cat.service';
import { InitFailureService } from './core/services/init-failure.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private _launchedFromLegacySite: boolean;

  public menuEnabled = true;
  public isSuperAdmin = false;
  public isEnabledEditableContent = false;

  @HostBinding('class.legacy-launched-app-root') get launchedFromLegacy() {
    return this._launchedFromLegacySite;
  }
  @HostBinding('class.self-host-launched-app-root') get selfHostLaunched() {
    return !this._launchedFromLegacySite;
  }

  constructor(
    private router: Router,
    private userService: UserService,
    private elementReferenceService: ElementRef,
    private appSettingsService: AppSettingsService,
    private urlSerializer: UrlSerializer,
    private affiliateService: AffiliateService,
    private store: Store<{ isEditContentMode: boolean }>,
    private initFailureService: InitFailureService,
  ) {
    this.handleIframeCommunication();
  }

  async ngOnInit() {
    this.appSettingsService.Settings.subscribe(appSettings => {
      this.userService.Init();
    });

    this.userService.OrganizerId = this.elementReferenceService.nativeElement.getAttribute('organizerId');

    this._launchedFromLegacySite =
      this.elementReferenceService.nativeElement.getAttribute('launchedFromLegacySite') || false;

    this.appSettingsService.setLaunchedFromLegacySite(this._launchedFromLegacySite);

    this.appSettingsService.setBaseUrl(this.elementReferenceService.nativeElement.getAttribute('rootUrl'));

    const menuEnabledAttribute = this.elementReferenceService.nativeElement.getAttribute('menuEnabled');
    this.menuEnabled = menuEnabledAttribute === null || menuEnabledAttribute === 'true' ? true : false;

    let initialRoute: string | UrlTree;

    // Navigate
    initialRoute = this.elementReferenceService.nativeElement.getAttribute('initialUrl') || '/';

    // Allow initial page to be passed by normal url, get link after #
    const passedUrl = window.location.hash.substr(1);
    if (passedUrl) {
      if (passedUrl != '/') initialRoute = this.urlSerializer.parse(passedUrl);
    }

    load(this.affiliateService.settings.cultureCode).then(locale => {
      registerLocaleData(locale.default, this.affiliateService.settings.cultureCode);
    });

    if (initialRoute) await this.router.navigateByUrl(initialRoute, { skipLocationChange: true });
    else this.router.initialNavigation();

    // TODO: hide editable content feature for now
    // this.userService.getUserData().then((userData) => {
    //   this.isSuperAdmin = userData.IsSuperAdmin;
    // });

    this.store
      .select(selectEditContentMode)
      .pipe(distinctUntilChanged())
      .subscribe(isEnabled => {
        this.isEnabledEditableContent = isEnabled;
      });

    ConfigCatService.init();
  }

  get MenuEnabled() {
    return this.menuEnabled;
  }

  get hasInitFailure(): boolean {
    return this.initFailureService.hasFailure;
  }

  public switchEditableContent(): void {
    this.store.dispatch(new SwitchEditContentAction(!this.isEnabledEditableContent));
  }

  private handleIframeCommunication(): void {
    // triggering route to the parent (when display it in iframe)
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      window.parent.postMessage({ type: 'ANGULAR_ROUTE', path: e.urlAfterRedirects }, '*');
    });

    // sync navigation from parent (react => angular in iframe)
    window.addEventListener('message', e => {
      if (e.data?.type === 'NAVIGATE') {
        this.router.navigateByUrl(e.data.path);
      }
    });
  }
}
